import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Badge,
  Button,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { CurrencyDollar, ArrowsClockwise, Receipt, Percent, ArrowSquareOut } from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct } from '../format';
import { IAuditoriaEmpresa, IImpostosApurados } from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { impostoDesc } from './_shared/impostos';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';

interface ImpostosApuradosScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Cruzamentos: navegar a outro submódulo (Notas, Impostos por Nota, Cruzamentos). */
  onScreen: (screen: string) => void;
}

// Cor consistente por imposto — usada nos DOIS gráficos (composição por sigla e
// apuração mensal por categoria) pra o mesmo tributo ter a MESMA cor. Casa tanto a
// sigla crua (SIMPLESN, ICMSA) quanto o label da categoria (Simples, ICMS Antecip).
const corImposto = (s: string): string => {
  const k = (s || '').toUpperCase();
  if (k.startsWith('SIMPLES')) return '#EC4899';
  if (k === 'ICMSA' || k.includes('ANTECIP')) return '#F59E0B';
  if (k.startsWith('DIFAL')) return '#0EA5E9';
  if (k === 'SUBTRI' || k === 'ST' || k.includes('-ST') || k.endsWith(' ST')) return '#6366F1';
  if (k === 'FCP') return '#14B8A6';
  if (k.startsWith('ICMS')) return UI.brand;
  if (k.startsWith('PIS')) return UI.pos;
  if (k.startsWith('COFI')) return UI.warn;
  if (k.startsWith('IRPJ')) return '#8B5CF6';
  if (k.startsWith('CSLL') || k.startsWith('CSOC')) return '#1AA6B7';
  if (k.startsWith('IPI')) return '#A855F7';
  if (k.startsWith('INSS')) return '#22C55E';
  if (k.startsWith('ISS')) return UI.neg;
  if (k === 'CRF' || k === 'CSRF' || k === 'COSIRF') return '#64748B';
  if (k.startsWith('IRRF')) return '#94A3B8';
  return '#94A3B8';
};

/** Período DATE_TRUNC ('YYYY-MM-DD' ou Date) → rótulo 'MM/AA'. */
const mesLabel = (m: string): string => {
  const [y, mo] = String(m ?? '').split('-');
  return mo && y ? `${mo}/${y.slice(2)}` : String(m ?? '');
};

export function ImpostosApuradosScreen({
  empresa,
  periodo,
  onScreen,
}: ImpostosApuradosScreenProps) {
  const { getImpostosApurados } = useAuditoriaInteligente();

  const { data, isFetching } = useQuery<IImpostosApurados>({
    queryKey: ['ai-impostos-apurados', { id: empresa?.id, ...periodo }],
    queryFn: getImpostosApurados,
    enabled: !!empresa,
  });

  // Pivota o formato longo (período, categoria, valor) → 1 linha por mês com uma coluna
  // por categoria + a lista de categorias presentes (ordenadas por total, com cor). Assim
  // o gráfico mostra TODAS as siglas com valor (ICMS Antecip, DIFAL, ST...), não 7 fixas.
  const { mensal, mensalCats } = useMemo(() => {
    const byMes: Record<string, Record<string, number | string>> = {};
    const totalByCat: Record<string, number> = {};
    (data?.apuracao_mensal ?? []).forEach(m => {
      const row = byMes[m.periodo] ?? { periodo: m.periodo, mes: mesLabel(m.periodo) };
      const v = num(m.valor);
      row[m.categoria] = (Number(row[m.categoria] ?? 0) || 0) + v;
      byMes[m.periodo] = row;
      totalByCat[m.categoria] = (totalByCat[m.categoria] ?? 0) + v;
    });
    const rows = Object.values(byMes).sort((x, y) =>
      String(x.periodo).localeCompare(String(y.periodo)),
    );
    const cats = Object.keys(totalByCat)
      .sort((x, y) => totalByCat[y] - totalByCat[x])
      .map(key => ({ key, color: corImposto(key) }));
    return { mensal: rows, mensalCats: cats };
  }, [data]);

  // Composição do imposto devido POR SIGLA (de por_categoria) — mostra TODOS os
  // tributos que têm valor (ICMSA, DIFAL, etc.), não só as famílias do gráfico mensal.
  const composicao = useMemo(() => {
    const bySig = new Map<string, number>();
    (data?.por_categoria ?? []).forEach(c => {
      bySig.set(c.imposto_sigla, (bySig.get(c.imposto_sigla) ?? 0) + num(c.valor_total));
    });
    return Array.from(bySig.entries())
      .map(([name, value]) => ({ name, value, color: corImposto(name) }))
      .filter(x => x.value > 0)
      .sort((x, y) => y.value - x.value);
  }, [data]);
  const totalComposicao = composicao.reduce((s, x) => s + x.value, 0);

  // Regime PIS/COFINS detectado (mais recente por sigla).
  const regimes = useMemo(() => {
    const map = new Map<string, { aliquota: number; regime: string }>();
    (data?.pis_cofins_regime ?? []).forEach(r => {
      map.set(r.imposto_sigla, { aliquota: num(r.aliquota), regime: r.regime_detectado });
    });
    return Array.from(map.entries());
  }, [data]);

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver os impostos apurados."
        icon={Receipt}
      />
    );
  }

  if (isFetching && !data) {
    return (
      <Box textAlign="center" py={16}>
        <Spinner />
      </Box>
    );
  }

  const a = data?.analise;

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
        <KpiCard
          label="Imposto devido"
          value={formatCurrency(a?.imposto_devido ?? 0)}
          icon={CurrencyDollar}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          onClick={() => onScreen('impostos-por-nota')}
          info={
            <InfoBloco
              oque="Total de imposto devido apurado no período (soma de todos os tributos)."
              dados="extract_impostos.valor_total (SUM) no período"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="A recuperar"
          value={formatCurrency(a?.a_recuperar ?? 0)}
          icon={ArrowsClockwise}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          onClick={() => onScreen('cruzamentos')}
          info={
            <InfoBloco
              oque="Crédito potencial a recuperar quando o imposto sobre entradas supera o de saídas."
              dados="extract_impostos: SUM(GREATEST(imposto_entradas - imposto_saidas, 0))"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Faturamento"
          value={formatCurrency(a?.faturamento ?? 0)}
          icon={Receipt}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          onClick={() => onScreen('notas')}
          info={
            <InfoBloco
              oque="Faturamento do período na regra do Relatório de Faturamento do Domínio: acumuladores com IFAT = S (saídas − devoluções + serviços)."
              dados="extract_acumuladores.saldo (ifat = 'S', mensal) — mês sem extração v2 usa as notas de venda + NFS-e"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Carga efetiva"
          value={pct(a?.carga_efetiva_pct, 2)}
          icon={Percent}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          onClick={() => onScreen('impostos-por-nota')}
          info={
            <InfoBloco
              oque="Carga tributária efetiva: percentual do imposto devido sobre o faturamento (regra do Domínio)."
              dados="extract_impostos.valor_total ÷ faturamento (acumuladores ifat = 'S') × 100"
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      {(composicao.length > 0 || mensal.length > 0 || regimes.length > 0) && (
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5} mb={5} alignItems="start">
          {composicao.length > 0 && (
            <Box
              bg={UI.glass}
              border="1px solid"
              borderColor={UI.glassBorder}
              borderRadius="16px"
              backdropFilter={UI.glassBlur}
              boxShadow={UI.glassShadow}
              p={5}
            >
              <Text fontSize="13px" fontWeight={700} mb={3} color={UI.ink}>
                Composição do imposto devido
              </Text>
              <Flex align="center" gap={4} wrap="wrap">
                <ResponsiveContainer width={180} height={180}>
                  <PieChart>
                    <Pie
                      data={composicao}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={48}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {composicao.map(s => (
                        <Cell key={s.name} fill={s.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <Box flex={1} minW="150px">
                  {composicao.map(s => (
                    <Flex key={s.name} align="center" gap={2} mb={1.5}>
                      <Box w="10px" h="10px" borderRadius="full" bg={s.color} flexShrink={0} />
                      <ChakraTooltip
                        label={impostoDesc(s.name)}
                        hasArrow
                        placement="top"
                        openDelay={150}
                        fontSize="12px"
                        maxW="280px"
                        borderRadius="8px"
                        px={3}
                        py={2}
                      >
                        <Text
                          fontSize="12px"
                          fontWeight={700}
                          color={UI.ink}
                          flex={1}
                          cursor="help"
                        >
                          {s.name}
                        </Text>
                      </ChakraTooltip>
                      <Text fontSize="12px" fontWeight={700} color={UI.muted}>
                        {pct(totalComposicao ? (s.value / totalComposicao) * 100 : 0)}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              </Flex>
            </Box>
          )}

          {mensal.length > 0 && (
            <Box
              bg={UI.glass}
              border="1px solid"
              borderColor={UI.glassBorder}
              borderRadius="16px"
              backdropFilter={UI.glassBlur}
              boxShadow={UI.glassShadow}
              p={5}
            >
              <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
                Apuração mensal por imposto
              </Text>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={mensal} maxBarSize={90} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke={UI.border} vertical={false} />
                  <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
                  <YAxis
                    fontSize={11}
                    stroke={UI.muted}
                    width={70}
                    tickFormatter={v => formatCurrency(v)}
                  />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  {mensalCats.map(c => (
                    <Bar key={c.key} dataKey={c.key} name={c.key} stackId="imp" fill={c.color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {regimes.length > 0 && (
            <Box
              bg={UI.glass}
              border="1px solid"
              borderColor={UI.glassBorder}
              borderRadius="16px"
              backdropFilter={UI.glassBlur}
              boxShadow={UI.glassShadow}
              p={5}
            >
              <Text fontSize="13px" fontWeight={700} mb={3} color={UI.ink}>
                Regime PIS/COFINS detectado
              </Text>
              {regimes.map(([sigla, r]) => (
                <Flex
                  key={sigla}
                  justify="space-between"
                  align="center"
                  py={2.5}
                  borderBottom="1px solid #EEF2F6"
                >
                  <Flex align="center" gap={2}>
                    <ChakraTooltip
                      label={impostoDesc(sigla)}
                      hasArrow
                      placement="top"
                      openDelay={150}
                      fontSize="12px"
                      maxW="280px"
                      borderRadius="8px"
                      px={3}
                      py={2}
                    >
                      <Text
                        fontSize="13px"
                        fontWeight={800}
                        color={UI.ink}
                        borderBottom="1px dashed"
                        borderColor={UI.faint}
                        cursor="help"
                      >
                        {sigla}
                      </Text>
                    </ChakraTooltip>
                    <Badge
                      style={{ background: UI.infoSoft, color: UI.brand }}
                      borderRadius="full"
                      px={2.5}
                      fontSize="11px"
                      fontWeight={700}
                      textTransform="none"
                    >
                      {r.regime}
                    </Badge>
                  </Flex>
                  <Text fontSize="13px" fontWeight={700} color={UI.muted}>
                    {pct(r.aliquota, 2)}
                  </Text>
                </Flex>
              ))}
              <Text fontSize="11px" color={UI.faint} mt={3}>
                Regime inferido pela alíquota efetiva — confirme no enquadramento da empresa.
              </Text>
            </Box>
          )}
        </Grid>
      )}

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5} mb={5}>
        <Box
          borderRadius="16px"
          overflow="hidden"
          bg="rgba(255,255,255,0.7)"
          backdropFilter="blur(14px) saturate(160%)"
          border="1px solid rgba(255,255,255,0.85)"
          boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
        >
          <Flex px={5} pt={4} pb={2} justify="space-between" align="center">
            <Text fontSize="13px" fontWeight={700} color={UI.ink}>
              Por categoria
            </Text>
            <Button
              size="xs"
              variant="ghost"
              color={UI.brand}
              rightIcon={<ArrowSquareOut size={12} weight="bold" />}
              onClick={() => onScreen('impostos-por-nota')}
            >
              Por nota
            </Button>
          </Flex>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Categoria</Th>
                  <Th>Imposto</Th>
                  <Th isNumeric>A recolher</Th>
                  <Th isNumeric>Base saídas</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data?.por_categoria ?? []).map(c => (
                  <Tr
                    key={`${c.categoria}-${c.imposto_sigla}-${c.valor_recolher}`}
                    _hover={{ bg: UI.rowHover }}
                  >
                    <Td maxW="200px" whiteSpace="normal">
                      {c.categoria}
                    </Td>
                    <Td fontWeight={700}>
                      <ChakraTooltip
                        label={impostoDesc(c.imposto_sigla)}
                        hasArrow
                        placement="top"
                        openDelay={150}
                        fontSize="12px"
                        maxW="280px"
                        borderRadius="8px"
                        px={3}
                        py={2}
                      >
                        <Text
                          as="span"
                          borderBottom="1px dashed"
                          borderColor={UI.faint}
                          cursor="help"
                        >
                          {c.imposto_sigla}
                        </Text>
                      </ChakraTooltip>
                    </Td>
                    <Td isNumeric fontWeight={700}>
                      {formatCurrency(c.valor_recolher)}
                    </Td>
                    <Td isNumeric>{formatCurrency(c.base_calc_saidas)}</Td>
                  </Tr>
                ))}
                {(data?.por_categoria ?? []).length === 0 && (
                  <Tr>
                    <Td colSpan={4} textAlign="center" py={6} color={UI.muted}>
                      Sem dados no período.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        <Box
          borderRadius="16px"
          overflow="hidden"
          bg="rgba(255,255,255,0.7)"
          backdropFilter="blur(14px) saturate(160%)"
          border="1px solid rgba(255,255,255,0.85)"
          boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
        >
          <Box px={5} pt={4} pb={2}>
            <Text fontSize="13px" fontWeight={700} color={UI.ink}>
              ICMS — débito × crédito (+ DIFAL / ST / FCP)
            </Text>
          </Box>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Período</Th>
                  <Th isNumeric>Débito</Th>
                  <Th isNumeric>Crédito</Th>
                  <Th isNumeric>A recolher</Th>
                  <Th isNumeric>DIFAL</Th>
                  <Th isNumeric>ST</Th>
                  <Th isNumeric>FCP</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data?.icms_deb_cred ?? []).map(r => (
                  <Tr key={r.periodo} _hover={{ bg: UI.rowHover }}>
                    <Td fontWeight={700}>{r.periodo}</Td>
                    <Td isNumeric>{formatCurrency(r.icms_debito)}</Td>
                    <Td isNumeric>{formatCurrency(r.icms_credito)}</Td>
                    <Td isNumeric fontWeight={700}>
                      {formatCurrency(r.icms_recolher)}
                    </Td>
                    <Td isNumeric color={num(r.difal) ? UI.ink : UI.muted}>
                      {num(r.difal) ? formatCurrency(r.difal) : '—'}
                    </Td>
                    <Td isNumeric color={num(r.st) ? UI.ink : UI.muted}>
                      {num(r.st) ? formatCurrency(r.st) : '—'}
                    </Td>
                    <Td isNumeric color={num(r.fcp) ? UI.ink : UI.muted}>
                      {num(r.fcp) ? formatCurrency(r.fcp) : '—'}
                    </Td>
                  </Tr>
                ))}
                {(data?.icms_deb_cred ?? []).length === 0 && (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={6} color={UI.muted}>
                      Sem ICMS no período.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Grid>
    </Box>
  );
}
